import React, { useState, useEffect, useCallback, useMemo } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { getDailyReceivedCapsules, getAllCapsules, editNotes, 
  retranscodeVideo, redownloadVideo, updateRMAStatus, getBinDirect, getCSVDirect, getXAM_AWS, getVideoURL,
  getCapsuleStats} from '../../apis/capsuleAPI'; 
import { extendSession, deleteToken } from '../../apis/sessionTokenAPI';
import {getAllLogs, addLog } from '../../apis/logAPI';
import { 
  DataGrid, 
} from '@mui/x-data-grid';
import { yellow, grey } from '@mui/material/colors';
import TextField        from '@mui/material/TextField';
import Button           from '@mui/material/Button';
import InputAdornment   from '@mui/material/InputAdornment';
import SearchIcon       from '@mui/icons-material/Search';
import EditIcon         from '@mui/icons-material/Edit';
import DescriptionIcon  from '@mui/icons-material/Description';
import ClearIcon        from '@mui/icons-material/Clear';
import IconButton       from '@mui/material/IconButton';

import './mainPage.css'; 
import { Tooltip } from '@mui/material';
import CapsuleStatusCell from '../../components/CapsuleStatueCell';
import SidebarNav from '../../components/SidebarNav';

const MainPage = () => {
  const issueTabs = ['Bin Issue', 'Video Issue', 'Download Issue'];
  const location = useLocation();
  const navigate = useNavigate(); // Initialize navigate

  // Better handling of state restoration from localStorage on refresh
  const [currentUser, setCurrentUser] = useState(() => {
    // Try to get user from location.state first (normal navigation)
    if (location.state && location.state.currentUser) {
      // Save to localStorage for future page refreshes
      localStorage.setItem('currentUser', JSON.stringify(location.state.currentUser));
      return location.state.currentUser;
    }
    
    // If not available (e.g., on page refresh), try localStorage
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        console.error('Error parsing saved user data:', e);
      }
    }
    
    // If all else fails, return an empty object
    return {};
  });
  
  const [capsules, setCapsules] = useState([]);
  const [filteredCapsules, setFilteredCapsules] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [activeTab, setActiveTab] = useState('In Process'); // New state for tab selection
  

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [totalPages, setTotalPages] = useState(1);


  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Separation of "Typing" vs "Searching" for capsule table
  const [inputValue, setInputValue] = useState('');        // Bound to TextField
  const [activeSearchQuery, setActiveSearchQuery] = useState(''); // Bound to API Call
  const isIssueTabActive = issueTabs.includes(activeTab);
  
  const [issueData, setIssueData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Issue Types',
        data: [],
        backgroundColor: ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'],
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  });
  const [transcodeData, setTranscodeData] = useState({
    labels: [],
    datasets: [
      {
        label: ['Transcoded', 'Not Transcoded'],
        data: [],
        backgroundColor: ['#7e3af2', '#1a1c23'],
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  });
  const [barData, setBarData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Capsule Status',
        data: [],
        backgroundColor: ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'],
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  });
  const [dateLineData, setDateLineData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Capsules per Date',
        data: [],
        borderColor: '#7e3af2',
        backgroundColor: 'rgba(126, 58, 242, 0.1)',
        fill: true,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#7e3af2',
        pointBorderWidth: 2,
      },
    ],
  });
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showRMANoteModal, setShowRMANoteModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showIssueTextModal, setShowIssueTextModal] = useState(false);
  const [currentCapsule, setCurrentCapsule] = useState(null);
  const [currentIssueText, setCurrentIssueText] = useState('');
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState('');
  const [allLogs, setAllLogs] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [transcodeProgress, setTranscodeProgress] = useState(0); // State for progress tracking
  const [csvFiles, setCsvFiles] = useState({}); // State for CSV file URLs
  const [binFiles, setBinFiles] = useState({}); // State for Binary file URLs
  const today = new Date().toLocaleDateString('en-CA');
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedSource, setSelectedSource] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [RMANote, setRMANote] = useState('');

  const [stats, setStats] = useState({
    total: 0,
    downloaded: 0,
    downloadFailed: 0,
    transcoded: 0,
    transcodeFailed: 0,
    transcodeSuccess: 0,
  });

  // define table columns
  const columns = useMemo(() => [
    { 
      field: 'SN', 
      headerName: 'SN', 
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 2.2,
      minWidth: 360,
      sortable: false,
      renderCell: (params) => {
        const capsule = params.row;
        if (!capsule) return null;
        const isVideoReviewed = capsule.video_status === 'Approved' || capsule.video_status === 'Rejected';
        const canShowIssueActions =
          !isVideoReviewed &&
          (capsule.status === 'Bin Issue' || capsule.status === 'Video Issue' || capsule.status === 'Bin and Video Issue');

        return (
          // 'flex-wrap' allows buttons to stack if the screen is too small
          <div className="flex items-center flex-wrap gap-2 py-2 w-full">
            
            {['Received in DLC', 'Uploading from DLC', 'Received in AWS'].includes(capsule.NBA_status) && (
              <span className="text-gray-500 italic text-sm">No Video Available</span>
            )}

            {(capsule.status === 'Video Issue' || capsule.status === 'Bin and Video Issue' || capsule.status === 'Bin Issue') && !isVideoReviewed && (
              <button 
                onClick={() => handleViewVideo(capsule)} 
                className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                Review Video
              </button>
            )}

            {canShowIssueActions && (
              <button 
                onClick={() => handleRMA(capsule)} 
                className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Contact Download Center
              </button>
            )}

            {canShowIssueActions && (
              <button 
                onClick={() => handleRedownload(capsule)}
                className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                Redownload
              </button>
            )}

            {!isVideoReviewed && (capsule.status === 'Processing Failed' || capsule.video_status === 'Rejected' || capsule.status === 'Video Issue' || capsule.status === 'Bin and Video Issue') && (
              <button 
                onClick={() => handleRetranscode(capsule)} 
                className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Retranscode
              </button>
            )}
          </div>
        );
      }
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1.6,
      minWidth: 320,
      renderCell: (params) => <CapsuleStatusCell row={params.row}/>
    },
    {
      field: 'issueLog',
      headerName: 'Issue',
      flex: 1.7,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        const rawIssueText = (params.row?.issueLog ?? '').toString().trim();
        const isEmptyInvestigationNote = /^INVESTIGATION NOTE\s*:?\s*$/i.test(rawIssueText);
        const issueText = rawIssueText && !isEmptyInvestigationNote ? rawIssueText : '-';
        return (
          <div className="w-full h-full flex items-center gap-2">
            <span className="truncate flex-1" title={issueText}>
              {issueText}
            </span>
            {issueText !== '-' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentCapsule(params.row);
                  setCurrentIssueText(issueText);
                  setShowIssueTextModal(true);
                }}
                className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
              >
                View
              </button>
            )}
          </div>
        );
      }
    },
    {
      field: 'created_date',
      headerName: 'Received Date',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const value = params.row?.createdAt;
        if (!value) return '-';
        const text = moment(value).format('YYYY-MM-DD');
        return text;
      }
    },
    {
      field: 'download_date',
      headerName: 'Download Date',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const value = params.row?.download_date;
        if (!value) return '-';
        const text = String(value);
        return text.includes('T') ? text.split('T')[0] : text.split(' ')[0];
      }
    },
    {
      field: 'transcode_date',
      headerName: 'Transcode Date',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const value = params.row?.transcode_date;
        if (!value) return '-';
        const text = String(value);
        return text.includes('T') ? text.split('T')[0] : text.split(' ')[0];
      }
    },
    {
      field: 'notes',
      headerName: 'Note',
      flex: 1.2,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const noteText = params.row?.notes || '';
        return (
          <div className="flex items-center justify-between w-full h-full gap-2">
            <span className="truncate flex-1" title={noteText}>
              {noteText || '-'}
            </span>
            <Tooltip title="Edit Note">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddNote(params.row);
                }}
                aria-label="Edit Note"
                className="text-gray-500 hover:text-blue-600"
              >
                <EditIcon sx={{ color: grey[800] }} />
              </button>
            </Tooltip>
          </div>
        );
      }
    },
    {
      field: 'logs',
      headerName: 'Activity',
      flex: 0.7,
      minWidth: 110,
      align: 'center',
      renderCell: (params) => (
        <Tooltip title="View log">
          <button
            onClick={() => handleViewAllLogs(params.row)}
            className="flex items-center h-full text-blue-600 hover:underline"
          >
            <DescriptionIcon sx={{ color: yellow[800] }}/>
          </button>
        </Tooltip>
      )
    }
  ], []);

  // Add token validation effect when the component mounts
  useEffect(() => {
    const validateToken = async () => {
      try {
        console.log("Validating session...");
        await extendSession();
        console.log("Session successfully validated");
      } catch (error) {
        console.error("Session validation failed:", error);
        // Only navigate away if it's a direct session validation error
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized: Session is invalid or expired");
          navigate('/login');
        } else {
          // For other errors, we don't redirect
          console.log("Non-auth error during session validation");
        }
      }
    };
    
    validateToken();
  }, [navigate]);

  const fetchDataFromDailyReceived = useCallback(async () => {
    try {
      let response;
      if(selectedDate) {
        const formattedDate = selectedDate.replace(/-/g, '');
        response = await getDailyReceivedCapsules(formattedDate);
      }
    } catch (dailyError) {
      console.error("Error fetching daily capsules:", dailyError);
    }
  }, [selectedDate]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Construct params using the ACTIVE search query, not the input value
      const params = {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      };
      
      if (activeSearchQuery) params.search = activeSearchQuery;
      if (selectedDate) params.date = selectedDate; 
      if (selectedSource !== 'all') params.source = selectedSource;
      if (activeTab && activeTab !== 'All')  params.status = activeTab;

      const response = await getAllCapsules(params);

      const data = response.data;

      setCapsules(data.capsules || []);
      setRowCount(data.totalCount || 0);

      const capsuleList = data.capsules;

      setCapsules(capsuleList);
      setFilteredCapsules(capsuleList); 
      calculateIssueData(capsuleList);
      calculateTranscodeData(capsuleList);
      calculateBarData(capsuleList);
      calculateDateLineData(capsuleList);
      // calculateDownloadProgress(capsuleList);
      // calculateTranscodeProgress(capsuleList);
    } catch (error) {
      console.error("Failed to fetch", error);
    } finally {
      setIsLoading(false);
    }
    // When pagination, active search, selected date, or active tab is updated, update this function to avoid recreate it every render
  }, [paginationModel, activeSearchQuery, selectedDate, selectedSource, activeTab]);

  const fetchDataStats = useCallback(async () => {
    try {
      const rawStats = await getCapsuleStats(selectedDate, selectedSource);
      const statsData = {
        total: Number(rawStats?.total || 0),
        downloaded: Number(rawStats?.downloaded || 0),
        downloadFailed: Number(rawStats?.downloadFailed || 0),
        transcoded: Number(rawStats?.transcoded || rawStats?.transcodeSuccess || 0),
        transcodeFailed: Number(rawStats?.transcodeFailed || 0),
        transcodeSuccess: Number(rawStats?.transcodeSuccess || rawStats?.transcoded || 0),
      };
      console.log("Stats data received from API:", statsData);
      setStats(statsData);
      
      const downloadProgress = statsData.total > 0 ? (statsData.downloaded / statsData.total) * 100 : 0;
      const computedTranscodeProgress = statsData.downloaded > 0 ? (statsData.transcodeSuccess / statsData.downloaded) * 100 : 0;

      setDownloadProgress(downloadProgress);
      setTranscodeProgress(computedTranscodeProgress);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setStats({
        total: 0,
        downloaded: 0,
        downloadFailed: 0,
        transcoded: 0,
        transcodeFailed: 0,
        transcodeSuccess: 0,
      });
      setDownloadProgress(0);
      setTranscodeProgress(0);
    }
  }, [selectedDate, selectedSource]);

  useEffect(() => {
    fetchData();
    // Only fetch capsule table data when table-related dependencies change
  }, [fetchData]);

  useEffect(() => {
    fetchDataStats();
    // Stats only depend on selectedDate via fetchDataStats
  }, [fetchDataStats]);

  // Just updates the text box, does NOT fetch capsule data
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Function to clear the search
  const handleClearSearch = () => {
    setInputValue('');           // Clear text box
    setActiveSearchQuery('');    // Clear actual search filter (triggers API)
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to page 0
  };

  // Function to handle search button click
  const handleSearchClick = () => {
    // Reset to page 0 ensures the user sees the first page of results
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setActiveSearchQuery(inputValue);
  };

  // Function to handle Enter key press (allow pressing "Enter" key to trigger search)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  useEffect(() => {
    fetchDataFromDailyReceived();
  }, []);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredCapsules.length / itemsPerPage));
    setCurrentPage(1); // Reset to first page when filter changes
  }, [filteredCapsules, itemsPerPage]);

  const calculateIssueData = (capsules) => {
    const issueCounts = capsules.reduce((acc, capsule) => {
      acc[capsule.status] = (acc[capsule.status] || 0) + 1;
      return acc;
    }, {});

    setIssueData({
      labels: Object.keys(issueCounts),
      datasets: [
        {
          label: 'Issue Types',
          data: Object.values(issueCounts),
          backgroundColor: ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'],
          borderWidth: 0,
          borderRadius: 4,
        },
      ],
    });
  };

  const calculateTranscodeData = (capsules) => {
    const transcodeCounts = capsules.reduce((acc, capsule) => {
      if (capsule.status === 'No Issue' || capsule.status === 'Video Issue') {
        acc.transcoded += 1;
      } else {
        acc.notTranscoded += 1;
      }
      return acc;
    }, { transcoded: 0, notTranscoded: 0 });

    setTranscodeData({
      labels: ['Transcoded', 'Not Transcoded'],
      datasets: [
        {
          labels: ['Capsules status'],
          data: [transcodeCounts.transcoded, transcodeCounts.notTranscoded],
          backgroundColor: ['#7e3af2', '#1a1c23'],
          borderWidth: 0,
          borderRadius: 4,
        },
      ],
    });
  };

  const calculateBarData = (capsules) => {
    const statusCounts = capsules.reduce((acc, capsule) => {
      acc[capsule.status] = (acc[capsule.status] || 0) + 1;
      return acc;
    }, {});

    setBarData({
      labels: Object.keys(statusCounts),
      datasets: [
        {
          label: 'Capsule Status',
          data: Object.values(statusCounts),
          backgroundColor: Object.keys(statusCounts).map((_, i) => 
            ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'][i % 4]),
          borderWidth: 0,
          borderRadius: 4,
        },
      ],
    });
  };

  // Function to calculate capsules per transcode date for line chart
  const calculateDateLineData = (capsules) => {
    const dateCounts = capsules.reduce((acc, capsule) => {
      if (capsule.transcode_date) {
        const date = new Date(capsule.transcode_date).toLocaleDateString();
        acc[date] = (acc[date] || 0) + 1;
      }
      return acc;
    }, {});

    // Sort dates chronologically
    const sortedDates = Object.keys(dateCounts).sort((a, b) => new Date(a) - new Date(b));
    
    setDateLineData({
      labels: sortedDates,
      datasets: [
        {
          label: 'Capsules per Date',
          data: sortedDates.map(date => dateCounts[date]),
          borderColor: '#7e3af2',
          backgroundColor: 'rgba(126, 58, 242, 0.1)',
          fill: true,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#7e3af2',
          pointBorderWidth: 2,
        },
      ],
    });
  };

  const handleViewVideo = (capsule) => {
    try {
      const newWindow = window.open('', '_blank');
      const params = new URLSearchParams();
      params.append('sn', capsule.SN);

      if (newWindow) {
        newWindow.location.href = `/video-player?${params.toString()}`;
      } else {
        throw new Error('Unable to open review video page')
      }
    } catch (error) {
      console.error('Error opening review video open:', error);
      alert(`Unable to open review video page${error.message ? ': ' + error.message : ''}`);
    }
  };

  const handleHome = () => {
    navigate('/main', { state: { currentUser } }); 
  }

  const handleUser = () => {
    console.log("Current user role is ", currentUser.role);
    if(["admin", "distributorAdmin"].includes(currentUser.role)) {
      navigate('/admin-user-management', { state: { currentUser } });
    } else if(["reviewer", "distributorRegular"].includes(currentUser.role)) {
      navigate('/reviewer-user-management', { state: { currentUser } });
    }
  };

  const handleCharts = () => {
    navigate('/charts', { state: { currentUser } });
  };

  const handleTools = () => {
    navigate('/tools', { state: { currentUser } });
  };

  const handleAddNote = (capsule, noteType) => {
    setCurrentCapsule(capsule);
    setNoteType(noteType);
    setNote(capsule.notes || ''); 
    setShowNoteModal(true);
  };

  const handleSubmitNote = async () => {
    try {
      await editNotes({SN: currentCapsule.SN, newNotes: note });
      setShowNoteModal(false);
      setNote('');
      fetchData(); // Refresh the capsules data after updating notes
    } catch (error) {
      console.error('Error logging notes:', error);
    }
  };

  const handleViewAllLogs = async (capsule) => {
    try {
      const logs = await getAllLogs(capsule.SN);
      setAllLogs(logs || []);
      setCurrentCapsule(capsule);
      setShowLogsModal(true);
    } catch (error) {
      console.error("Failed to retrieve logs:", error);
      setAllLogs([]);
      setCurrentCapsule(capsule);
      setShowLogsModal(true);
    }
  };

  const logOut = async() => {
    try {
      // First navigate, then clean up session
      navigate('/login');
      
      // Try to delete the session on the server
      try {
        await deleteToken();
        console.log('Session deleted successfully');
      } catch (err) {
        // If this fails, it's not critical - user is already logged out on client
        console.error('Error deleting session on server:', err);
      }
    } catch(error) {
      console.error('Error logging out: ', error)
    }
  };

  const handleRetranscode = async (capsule) =>
  {
    // currently not implemented, only a placeholder
    try {
      await retranscodeVideo({SN: capsule.SN});
      await addLog({SN: capsule.SN, log_type: "General Log", log_info: "Retranscoding started"});
      console.log("Retranscoding capsule", capsule.SN);
      fetchData(); // Refresh the list after retranscoding
    } catch (error) {
      console.error('Error retranscoding:', error);
    }
  }

  const handleRMA = async (capsule) => {
    try {
      console.log("Setting investigation status for capsule", capsule.SN);
      if(!capsule.RMA_status) {
        const response = await updateRMAStatus(capsule.SN, true);
        console.log("Investigation status updated for capsule", capsule.SN, response);
        fetchData(); // Refresh the list after setting investigation status
      }
      
      // If we're turning RMA_status to true, show the notes modal
        setCurrentCapsule(capsule);
        setRMANote(capsule.RMA_note || '');
        setShowRMANoteModal(true);
    } catch (error) {
      console.error('Error setting investigation status:', error);
    }
  }

  const handleAddRMANote = (capsule, noteType) => {
    setCurrentCapsule(capsule);
    setRMANote(capsule.notes || ''); 
    setShowRMANoteModal(true);
  };

  const handleRedownload = async (capsule) => {
    try {
      setCurrentCapsule(capsule);
      setShowUploadModal(true);
    } catch (error) {
      console.error('Error preparing redownload:', error);
    }
  };

  const handleDownloadBinFiles = async () => {
    try {
      const zip = new JSZip();
      const fetchPromises = [];
      const fileName = currentCapsule.SN;
      const files = binFiles.binFiles || binFiles;
      
      const fetchFile = async (url, fileName) => {
        try {
          console.log(`Fetching ${fileName} from URL: ${url}`);
          const response = await fetch(url, { mode: 'cors' });

          if (!response.ok) {
            console.warn(`Failed to fetch ${fileName}: ${response.statusText}`);
            return false;
          }

          const blob = await response.blob();
          zip.file(fileName, blob);
          return true;
        } catch (error) {
          console.warn(`Error fetching ${fileName}:`, error);
          return false;
        }
      };

      if (files.main_bin) {
        fetchPromises.push(fetchFile(files.main_bin, `${fileName}.bin`));
      }
      if (files.supp_1_bin) {
        fetchPromises.push(fetchFile(files.supp_1_bin, `${fileName}_supp_1.bin`));
      }
      if (files.supp_2_bin) {
        fetchPromises.push(fetchFile(files.supp_2_bin, `${fileName}_supp_2.bin`));
      }
      if (files.supp_3_bin) {
        fetchPromises.push(fetchFile(files.supp_3_bin, `${fileName}_supp_3.bin`));
      }

      console.log('Processing all fetch requests...');
      const results = await Promise.all(fetchPromises);

      const successCount = results.filter(Boolean).length;
      console.log(`${successCount} bin files successfully fetched.`);

      if (successCount === 0 && fetchPromises.length > 0) {
        throw new Error('Could not fetch any bin files');
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${currentCapsule.SN}_bin_files.zip`);
    } catch (error) {
      console.error('Error downloading bin files:', error);
      alert('Error downloading bin files: ' + error.message);
    }
  }


  const handleDownloadCSVFiles = async () => {
  try {
    const zip = new JSZip();
    const fetchPromises = [];

    const files = csvFiles.csvFiles || csvFiles; // flatten if needed
    console.log('Resolved CSV files:', files);

    const fetchFile = async (url, fileName) => {
      try {
        console.log(`Fetching ${fileName} from URL: ${url}`);
        const response = await fetch(url, { mode: 'cors' });

        if (!response.ok) {
          console.warn(`Failed to fetch ${fileName}: ${response.statusText}`);
          return false;
        }

        const blob = await response.blob();
        zip.file(fileName, blob);
        return true;
      } catch (error) {
        console.warn(`Error fetching ${fileName}:`, error);
        return false;
      }
    };

    if (files.main_csv) {
      fetchPromises.push(fetchFile(files.main_csv, 'main.csv'));
    }
    if (files.supp_1_csv) {
      fetchPromises.push(fetchFile(files.supp_1_csv, 'supp_1.csv'));
    }
    if (files.supp_2_csv) {
      fetchPromises.push(fetchFile(files.supp_2_csv, 'supp_2.csv'));
    }
    if (files.supp_3_csv) {
      fetchPromises.push(fetchFile(files.supp_3_csv, 'supp_3.csv'));
    }
    if (files.combined_csv) {
      fetchPromises.push(fetchFile(files.combined_csv, 'combined.csv'));
    }

    console.log('Processing all fetch requests...');
    const results = await Promise.all(fetchPromises);

    const successCount = results.filter(Boolean).length;
    console.log(`${successCount} files successfully fetched.`);

    if (successCount === 0 && fetchPromises.length > 0) {
      throw new Error('Could not fetch any CSV files');
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${currentCapsule.SN}_csv_files.zip`);
  } catch (error) {
    console.error('Error downloading CSV files:', error);
    alert('Error downloading CSV files: ' + error.message);
  }
};


  // Function to handle date change
  const handleDateChange = (e) => {
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset page
    setSelectedDate(e.target.value); // Triggers fetch immediately via Effect
  };

  // Function to clear date filter
  const clearDateFilter = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectedDate('');
  };

  const handleSourceChange = (e) => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectedSource(e.target.value);
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    setUploadedFiles(files);
  };

  const handleUploadSubmit = async () => {
    if (!uploadedFiles || !currentCapsule) return;

    try {
      const formData = new FormData();
      formData.append('SN', currentCapsule.SN);
      
      // Append each file from the folder
      for (let i = 0; i < uploadedFiles.length; i++) {
        formData.append('binFiles', uploadedFiles[i]);
      }

      console.log("Sending redownload request with SN:", currentCapsule.SN);
      console.log("Number of files:", uploadedFiles.length);
      
      await redownloadVideo(currentCapsule.SN, formData);
      
      setShowUploadModal(false);
      setUploadedFiles(null);
      fetchData();
    } catch (error) {
      console.error('Error redownloading files:', error);
      alert('Failed to redownload files: ' + error.message);
    }
  };

  const handleDownloadXAM = async () => {
    try {
      // 1. Get the URL from your backend (Presigned URL)
      const response = await getXAM_AWS(currentCapsule.SN);
      const downloadUrl = response.xam_url;

      // 2. Create a temporary link pointing DIRECTLY to that URL
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // 3. Set attributes to force download
      link.setAttribute('download', `${currentCapsule.SN}.xam`);
      
      // 4. Append, Click, and Cleanup (to avoid memory leak)
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading XAM file:", error);
      alert('Error downloading XAM file: ' + error.message);
    }
  }

  return (
    <div className={`main-page-container theme-transition`}>
      <SidebarNav
        activePage="home"
        onHome={handleHome}
        onCharts={handleCharts}
        onTools={handleTools}
        onUsers={handleUser}
        onLogout={logOut}
      />

      <div className="main-content">
        <header className="header">
          <div>
            <h2 className="text-lg font-semibold">Welcome, {currentUser.first_name}</h2>
          </div>
        </header>

        <div className="container space-y-6">
          <h2 className="page-title">Capsule Info</h2>

          <div className="flex flex-col md:flex-row md:items-center md:justify-start md:space-x-4 mb-6">
            <div className="progress-container md:flex-1 md:max-w-md">
              <h3 className="progress-label">AWS Upload Progress</h3>
              <div className="progress-bar">
                <div
                  className="progress-value"
                  style={{ width: `${downloadProgress}%` }}
                ></div>
              </div>
              <div className="progress-fraction">
                {stats.downloaded} / {stats.total}
              </div>
              <div className="progress-stats">
                <span className="success-stat">Upload Success: {stats.downloaded}</span>
                <span className="failed-stat">Download Failure: {stats.downloadFailed}</span>
              </div>
            </div>
            
            
            {/* Progress Bar Card */}
            <div className="progress-container md:flex-1 md:max-w-md">
              <h3 className="progress-label">Transcode Progress</h3>
              <div className="progress-bar">
                <div
                  className="progress-value"
                  style={{ width: `${transcodeProgress}%` }}
                ></div>
              </div>
              <div className="progress-fraction">
                {stats.transcodeSuccess} / {stats.downloaded}
              </div>
              <div className="progress-stats">
                <span className="success-stat">Transcode Success: {stats.transcodeSuccess}</span>
                <span className="failed-stat">Transcode Failed: {stats.transcodeFailed}</span>
              </div>
            </div>
          </div>

          {/* Search and Date Filter Section */}
          <div className="flex flex-wrap items-end gap-6 mb-6">
            {/* 1. Search Section */}
            <div className="flex items-center gap-2">
              <TextField
                label="Search SN"
                variant="outlined"
                size="small"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Type Capsule SN Here"
                slotProps={{
                  input: {
                    // Left side: Search Icon
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className="text-gray-400" />
                      </InputAdornment>
                    ),
                    
                    // Right side: Clear Button (Only shows if there is text)
                    endAdornment: inputValue && (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleClearSearch} 
                          edge="end" 
                          size="small"
                          title="Clear search"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
              }}
              />
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSearchClick}
                sx={{ height: '40px' }} // Match height of TextField
              >
                Search
              </Button>
            </div>

            {/* 2. Date Filter Section */}
            <div className="filter-container md:mb-0">
              <label htmlFor="date-filter" className="filter-label block text-sm font-medium text-gray-700 mb-1">
                Filter by Download Date:
              </label>
              <div className="date-input-group flex items-center gap-2">
                <input 
                  type="date" 
                  id="date-filter" 
                  value={selectedDate} 
                  onChange={handleDateChange}
                  className="date-input border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-[40px]"
                />
                {selectedDate && (
                  <button 
                    onClick={clearDateFilter} 
                    className="clear-date-btn text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="filter-container source-filter-container md:mb-0">
              <label htmlFor="source-filter" className="source-filter-label text-sm font-medium text-gray-700">
                Source:
              </label>
              <select
                id="source-filter"
                value={selectedSource}
                onChange={handleSourceChange}
                className="filter-select source-filter-select h-[40px]"
              >
                <option value="all">All</option>
                <option value="dlc">Download Center</option>
                <option value="xbin">CapsoCloud XBIN</option>
              </select>
            </div>
          </div>

          {/* NBA Status Tabs */}
          <div className="tabs-container">
            <div className="tabs">
            <button 
                className={`tab ${activeTab === 'All' ? 'active' : ''}`}
                onClick={() => setActiveTab('All')}
              >
                All
              </button>
              <button 
                className={`tab ${activeTab === 'In Process' ? 'active' : ''}`} 
                onClick={() => setActiveTab('In Process')}
              >
                In Process
              </button>
              <button 
                className={`tab ${activeTab === 'Video Available to Customer' ? 'active' : ''}`}
                onClick={() => setActiveTab('Video Available to Customer')}
              >
                Video Available
              </button>
              <button 
                className={`tab ${isIssueTabActive ? 'active' : ''}`}
                onClick={() => setActiveTab('Bin Issue')}
              >
                Issue
              </button>
            </div>
            {isIssueTabActive && (
              <div className="tabs issue-subtabs">
                <button 
                  className={`tab ${activeTab === 'Bin Issue' ? 'active' : ''}`}
                  onClick={() => setActiveTab('Bin Issue')}
                >
                  Bin Issue
                </button>
                <button 
                  className={`tab ${activeTab === 'Video Issue' ? 'active' : ''}`}
                  onClick={() => setActiveTab('Video Issue')}
                >
                  Video Issue
                </button>
                <button 
                  className={`tab ${activeTab === 'Download Issue' ? 'active' : ''}`}
                  onClick={() => setActiveTab('Download Issue')}
                >
                  Download Issue
                </button>
              </div>
            )}
          </div>
          {/* Data Table */}
          <div className="card capsule-list-card">
            <div className="card-header">
              <h3 className="card-title">Capsule List</h3>
              <div className="filter-summary">
                {activeTab !== 'Overall' && (
                  <span className="filter-badge">{activeTab}</span>
                )}
                {selectedStatus !== 'All' && (
                  <span className="filter-badge">{selectedStatus}</span>
                )}
                {selectedDate && (
                  <span className="filter-badge date-badge">
                    Date: {selectedDate}
                  </span>
                )}
                {selectedSource !== 'all' && (
                  <span className="filter-badge">
                    Source: {selectedSource.toUpperCase()}
                  </span>
                )}
                <span className="capsule-count">{rowCount} capsules</span>
              </div>
            </div>
            <div className="table-container capsule-grid-container">
              <DataGrid
                rows={filteredCapsules || []} // Safe default
                columns={columns}
                // Uniqueness Check: Fallback to random if SN is missing to prevent crash
                getRowId={(row) => row.SN || Math.random()}
                
                // Dynamic Row Height: Essential for the buttons to wrap correctly
                getRowHeight={() => 'auto'} 
                
                loading={isLoading}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50]}

                // Show total count from the server
                rowCount={rowCount}
                paginationMode="server"
                // Pagination info showing at the footer
                slotProps={{
                  pagination: {
                    labelDisplayedRows: ({ from, to, count }) => {
                      console.log('Here is total count:', count)
                      return `Showing ${from}–${to} of ${count} total capsules`;
                    },
                    labelRowsPerPage: "Capsules per page:",
                  },
                }}

                sx={{
                  height: '100%',
                  '& .MuiDataGrid-columnHeaders': {
                    borderBottom: '1px solid #d1d5db',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    borderRight: '1px solid #e5e7eb',
                  },
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-columnHeader:last-of-type': {
                    borderRight: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex', 
                    alignItems: 'center',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    borderRight: '1px solid #e5e7eb',
                  },
                  '& .MuiDataGrid-cell:last-of-type': {
                    borderRight: 'none',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflowY: 'auto !important',
                    overscrollBehavior: 'contain',
                  },
                }}
              />
            </div>
          </div>
          
        </div>
      </div>

      {showNoteModal && (
        <div className="note-modal">
          <div className="note-modal-content">
            <h2 className="card-title">Add Note for Capsule {currentCapsule.SN} ({noteType})</h2>
            <textarea 
              value={note} 
              onChange={(e) => setNote(e.target.value)} 
              className="note-textarea"
            />
            <div
              className="button-box"
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <button
                onClick={handleSubmitNote}
                className="button"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-primary-foreground)',
                  borderColor: 'var(--color-primary)',
                  width: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                Submit
              </button>
              <button
                onClick={() => setShowNoteModal(false)}
                className="button"
                style={{
                  backgroundColor: 'var(--color-muted)',
                  color: 'var(--color-muted-foreground)',
                  borderColor: 'var(--color-muted)',
                  width: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showRMANoteModal && (
        <div className="note-modal">
          <div className="note-modal-content">
            <h2 className="card-title">Add Investigation Note for Capsule {currentCapsule.SN}</h2>
            <textarea 
              value={RMANote} 
              onChange={(e) => setRMANote(e.target.value)} 
              className="note-textarea"
            />
            <div className="button-box">
              <button 
                onClick={async () => {
                  try {
                    // Update investigation notes in the database
                    await updateRMAStatus(currentCapsule.SN, true, RMANote);
                    setShowRMANoteModal(false);
                    fetchData(); // Refresh capsules to show updated notes
                  } catch (error) {
                    console.error('Error saving investigation notes:', error);
                  }
                }} 
                className="button"
              >
                Submit
              </button>
              <button onClick={() => setShowRMANoteModal(false)} className="button button-danger">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="logs-modal">
          <div className="logs-modal-content">
            <h2 className="card-title">Activity Logs for Capsule {currentCapsule.SN}</h2>
            
            {allLogs.length > 0 ? (
              <div className="logs-table-container">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>Log Type</th>
                      <th>Action</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allLogs.map((log, index) => (
                      <tr key={index}>
                        <td className="log-type-cell">
                          <span className={`log-type-badge ${log.log_type?.replace(/\s+/g, '-').toLowerCase()}`}>
                            {log.log_type}
                          </span>
                        </td>
                        <td className="log-action-cell">{log.log_info || 'No action recorded'}</td>
                        <td className="log-time-cell">
                          {log.log_date 
                            ? new Date(log.log_date).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : 'No date recorded'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-logs-message">
                <p>No logs available for this capsule.</p>
              </div>
            )}

            {/* File download buttons for specific log types */}
            {(currentCapsule.status === 'Bin Issue' || currentCapsule.status === 'Bin and Video Issue') && allLogs.some(log => log.log_type === 'Bin Issue Log') && (
              <div className="file-buttons">
                <h3 className="card-title">Download Files</h3>
                <div className="file-buttons-row">
                  <button 
                    onClick={async () => {
                      try {
                        const fetchedCsvFiles = await getCSVDirect(currentCapsule.SN);
                        setCsvFiles(fetchedCsvFiles);
                        handleDownloadCSVFiles();
                      } catch (error) {
                        console.error("Error downloading CSV files:", error);
                        alert('Error downloading CSV files: ' + error.message);
                      }
                    }}
                    className="button button-info small-btn"
                  >
                    Download CSV Files
                  </button>
                  <button 
                    onClick={async () => {
                      try {
                        const fetchedBinFiles = await getBinDirect(currentCapsule.SN);
                        setBinFiles(fetchedBinFiles);
                        handleDownloadBinFiles();
                      } catch (error) {
                        console.error("Error downloading bin files:", error);
                        alert('Error downloading bin files: ' + error.message);
                      }
                    }}
                    className="button small-btn"
                  >
                    Download Bin Files
                  </button>
                </div>
              </div>
            )}

            {/* XAM file download for Video Issue */}
            {(currentCapsule.status === 'Video Issue' || currentCapsule.status === 'Bin and Video Issue') && allLogs.some(log => log.log_type === 'Video Issue Log') && (
              <div className="file-buttons">
                <h3 className="card-title">XAM File</h3>
                <div className="file-buttons-row">
                  <button 
                    onClick={handleDownloadXAM}
                    className="button button-info small-btn"
                  >
                    Download XAM File
                  </button>
                </div>
              </div>
            )}

            <div className="button-box">
              <button onClick={() => setShowLogsModal(false)} className="button">Close</button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="note-modal">
          <div className="note-modal-content">
            <h2 className="card-title">Redownload Bin Files for Capsule {currentCapsule.SN}</h2>
            <div className="upload-container">
              <input
                type="file"
                webkitdirectory="true"
                directory=""
                multiple
                onChange={handleFileUpload}
                className="file-input"
              />
              <div className="upload-progress">
                {uploadProgress > 0 && (
                  <div className="progress-bar">
                    <div
                      className="progress-value"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
            <div className="button-box">
              <button 
                onClick={handleUploadSubmit}
                disabled={!uploadedFiles}
                className="button"
              >
                Redownload
              </button>
              <button 
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedFiles(null);
                }} 
                className="button button-danger"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showIssueTextModal && (
        <div className="note-modal">
          <div className="note-modal-content">
            <h2 className="card-title">
              Issue Details{currentCapsule?.SN ? ` for Capsule ${currentCapsule.SN}` : ''}
            </h2>
            <textarea
              value={currentIssueText || ''}
              readOnly
              className="note-textarea"
            />
            <div className="button-box">
              <button
                onClick={() => {
                  setShowIssueTextModal(false);
                  setCurrentIssueText('');
                }}
                className="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MainPage;
